import { reportError } from '../telemetry/report';
import { markDoneLocally } from './doneLocal';
import { fetchWithTimeout, UPLOAD_TIMEOUT_MS } from '../api/fetchWithTimeout';
import { hasPendingScanFor, markScanNeeded } from './idscans';
import {
  enqueue, peekAll, remove, markFailure, getToken, clearToken, type QueuedSubmission,
} from './db';

/**
 * Queue flusher — runs in BOTH contexts:
 *  - the page (on 'online' events and app foregrounding)
 *  - the service worker (Background Sync 'jw-completions' tag)
 *
 * Deliberately dependency-free (no client.ts import: that module touches
 * window). Everything here uses fetch + IDB only, so it bundles cleanly
 * into the worker.
 *
 * fix_age_s honesty rule (spec §6): the GPS fix is stamped when CAPTURED.
 * fix_age_s is computed at SUBMISSION time, so the server always learns
 * exactly how stale the coordinates are. Server time remains authoritative
 * for completed_at.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;
const MAX_ATTEMPTS = 50; // keep retrying ~an entire shift; then surface to driver

/**
 * The session has expired. Thrown, rather than returned, so it cannot be
 * mistaken for a verdict on a delivery at any point in the call chain.
 *
 * This is the one failure that no amount of retrying fixes and that the driver
 * alone can clear. It used to have no handling whatsoever: one driver was
 * refused at /uploads/sign seventeen times across a full shift, finished 0 of
 * 10 stops, and was never told why.
 */
export class SessionExpired extends Error {
  constructor() { super('session_expired'); }
}

/** 401/403 from our own API means the token is dead, not that the stop failed. */
function throwIfExpired(status: number): void {
  if (status === 401 || status === 403) throw new SessionExpired();
}

/**
 * The stop this submission belongs to no longer exists for this driver.
 *
 * Thrown for a 404, which the server returns as `task_not_found_or_not_yours`.
 * It happens after a device is re-enrolled, after a route is rebuilt, or when a
 * queued item survives past the day it belonged to.
 *
 * Retrying cannot fix it — the task is not coming back. One phone spent an
 * evening firing the same six uploads into a wall because a 404 was treated as
 * an ordinary failure, burning one of fifty attempts each time and filling the
 * log with noise nobody could interpret.
 */
class TaskGone extends Error {
  constructor() { super('task_gone'); }
}

function throwIfGone(status: number): void {
  if (status === 404) throw new TaskGone();
}

async function authedFetch(path: string, init: RequestInit, token: string): Promise<Response> {
  return fetchWithTimeout(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...init.headers,
    },
  });
}

/*
 * Read a stored image fully into memory before sending it (4 Oct).
 *
 * The proof sits in the phone's own storage (IndexedDB) while it waits. On
 * Apple's browser a Blob read back from there can fail to STREAM as an upload
 * body: the PUT simply never finishes, times out at 45 s, and is retried for
 * ever -- Yeisson Perez's iPad held a signature that way for 21 hours, every
 * attempt "stage=signature_upload ms=45000". Reading the bytes first takes that
 * path out of the browser's hands, and a file that truly cannot be read now
 * fails in 10 s with a reason instead of hanging the whole queue.
 */
async function readAll(blob: Blob): Promise<ArrayBuffer> {
  const buf = await Promise.race([
    blob.arrayBuffer(),
    new Promise<never>((_, rej) => setTimeout(() => rej(new Error('blob_unreadable')), 10_000)),
  ]);
  if (buf.byteLength === 0 && blob.size > 0) throw new Error('blob_unreadable');
  return buf;
}

async function uploadProof(
  token: string, taskId: string, kind: 'photo' | 'signature', src: Blob | { bytes: ArrayBuffer; type: string },
): Promise<string> {
  // Stored bytes need no reading; an older queued Blob is read in full first.
  const blob = src instanceof Blob ? src : new Blob([src.bytes], { type: src.type });
  const bytes = src instanceof Blob ? await readAll(src) : src.bytes;
  const signRes = await authedFetch('/uploads/sign', {
    method: 'POST',
    body: JSON.stringify({ taskId, kind, contentType: blob.type || 'image/jpeg' }),
  }, token);
  throwIfExpired(signRes.status);
  throwIfGone(signRes.status);
  if (!signRes.ok) throw new Error(`sign_failed_${signRes.status}`);
  const { uploadUrl, objectPath, requiredHeaders } = (await signRes.json()) as
    { uploadUrl: string; objectPath: string; requiredHeaders?: Record<string, string> };

  // The signature covers a specific set of headers; sending anything else --
  // or leaving one out -- is a 403 from GCS, not a retryable failure. The
  // server tells us what it signed rather than us guessing.
  const putRes = await fetchWithTimeout(uploadUrl, {
    method: 'PUT',
    headers: requiredHeaders ?? { 'Content-Type': blob.type || 'image/jpeg' },
    // A fresh in-memory copy, not the stored one -- see readAll().
    body: new Blob([bytes], { type: blob.type || 'image/jpeg' }),
  }, UPLOAD_TIMEOUT_MS);   // a photo on a weak connection deserves longer
  if (!putRes.ok) throw new Error(`upload_failed_${putRes.status}`);
  return objectPath;
}

/**
 * The server's verdict, with the REASON kept.
 *
 * This used to collapse to a bare 'rejected', so the driver was told only that
 * something went wrong -- for a problem ("you are 50 m away") they could have
 * fixed in ten seconds by walking closer.
 */
export type Verdict =
  | { status: 'done' }
  | { status: 'retry' }
  | { status: 'rejected'; reason: string; distanceM?: number; radiusM?: number };

async function submitOne(item: QueuedSubmission, token: string): Promise<Verdict> {
  progress.stage = item.kind; progress.t0 = Date.now(); progress.photoKB = 0;
  if (item.kind === 'skip') {
    const res = await authedFetch(`/tasks/${item.taskId}/skip`, {
      method: 'POST',
      body: JSON.stringify({
        reason: item.skipReason,
        lat: item.fix?.lat ?? null,
        lng: item.fix?.lng ?? null,
        note: item.skipNote,
      }),
    }, token);
    if (res.ok) return { status: 'done' };
    throwIfExpired(res.status);
    throwIfGone(res.status);
    return res.status >= 500
      ? { status: 'retry' }
      : { status: 'rejected', reason: 'skip_refused' };
  }

  // kind === 'complete'
  /*
   * A copy that has failed before may be a SECOND copy of a delivery the office
   * already has (4 Oct: Yeisson Perez, Mercedes Romero — copies of finished
   * stops retrying for 21 and 68 hours, slowing everything behind them). Ask
   * first; if the stop is already completed, this copy is done. Any other
   * answer, or no answer, carries on exactly as before.
   */
  if (item.attempts >= 1) {
    progress.stage = 'state_check';
    try {
      const st = await authedFetch(`/tasks/${item.taskId}/state`, { method: 'GET' }, token);
      throwIfExpired(st.status);
      if (st.ok) {
        const body = (await st.json()) as { status?: string };
        if (body.status === 'completed') {
          reportError('diag_sync', 'sync', { ok: true, duplicate: true, tries: item.attempts + 1 });
          /*
           * A repeat of a delivered stop, with WHERE it was pressed (10 Oct).
           * If that was another hospital, a delivery was made on the wrong
           * screen; the office sees it in Red flags the next morning.
           */
          reportError('diag_repeat', 'sync', JSON.stringify({
            task: item.taskId, lat: item.fix?.lat ?? null, lng: item.fix?.lng ?? null,
            acc: item.fix ? Math.round(item.fix.accuracyM) : null, pressed: item.queuedAt }));
          return { status: 'done' };
        }
      }
    } catch (e) {
      if (e instanceof SessionExpired) throw e;
      /* no answer: carry on with the normal send */
    }
  }
  progress.stage = 'waiting_for_fix';
  progress.photoKB = item.photoBytes ? Math.round(item.photoBytes.byteLength / 1024)
    : item.photoBlob ? Math.round(item.photoBlob.size / 1024) : 0;
  /*
   * A missing GPS fix is NOT a verdict on the delivery, and it must never
   * destroy it. This used to return 'rejected', and the rejected branch calls
   * remove() -- so a driver who captured everything in a basement had the whole
   * delivery deleted because the chip could not see a satellite. Silently.
   *
   * 'retry' keeps the item. The page attaches a fix as soon as one is available
   * (see syncManager), and until then it simply waits.
   */
  if (!item.fix) return { status: 'retry' };
  /*
   * Held in a const because `item` is reassigned below as uploads complete,
   * and each reassignment discards what the compiler knew about item.fix.
   * The fix is settled by this line; nothing after it changes it.
   */
  const fix = item.fix;

  /*
   * A completion with no photo is a genuine gap in the record rather than a
   * passing condition, and the driver is told so.
   *
   * `photoPath` is checked TOO, and forgetting it here would be catastrophic:
   * once an upload succeeds the blob is dropped, so an item on its second
   * attempt legitimately has no blob. Reading only `photoBlob` would throw away
   * a delivery whose photo is already safely in the bucket.
   */
  if (!item.photoBlob && !item.photoBytes && !item.photoPath) {
    return { status: 'rejected', reason: 'rejected_missing_proof' };
  }

  /*
   * Upload once, remember where it went.
   *
   * This used to re-upload both images on every attempt. One phone sent
   * thirty-two images to complete one stop on 17 Sep, because each of his
   * sixteen retries started from nothing. Worse, it meant the photo and the
   * signature stayed in the phone's storage for as long as the item did --
   * and the moment the app has been dying is exactly when it returns to the
   * route screen with a just-sent stop still holding its proof.
   *
   * Saving the path back into the item and dropping the blob fixes both. A
   * retry now only re-sends the completion itself, which is a few hundred
   * bytes.
   */
  let proofPhotoPath = item.photoPath ?? null;
  if (!proofPhotoPath && (item.photoBytes || item.photoBlob)) {
    progress.stage = 'photo_upload';
    proofPhotoPath = await uploadProof(token, item.taskId, 'photo',
      item.photoBytes ? { bytes: item.photoBytes, type: item.photoType || 'image/jpeg' } : item.photoBlob!);
    item = { ...item, photoPath: proofPhotoPath, photoBlob: null, photoBytes: null };
    await enqueue(item);
  }

  let signaturePath = item.signaturePath ?? null;
  if (!signaturePath && (item.signatureBytes || item.signatureBlob)) {
    progress.stage = 'signature_upload';
    signaturePath = await uploadProof(token, item.taskId, 'signature',
      item.signatureBytes ? { bytes: item.signatureBytes, type: item.signatureType || 'image/jpeg' } : item.signatureBlob!);
    item = { ...item, signaturePath, signatureBlob: null, signatureBytes: null };
    await enqueue(item);
  }

  progress.stage = 'confirm';
  const res = await authedFetch(`/tasks/${item.taskId}/complete`, {
    method: 'POST',
    body: JSON.stringify({
      lat: fix.lat,
      lng: fix.lng,
      accuracyM: fix.accuracyM,
      /*
       * HOW OLD THE READING WAS WHEN THE DRIVER PRESSED SUBMIT.
       *
       * This used to be measured from Date.now() -- the moment the phone
       * finally got signal -- so every minute spent waiting in a basement
       * counted against the driver. One stood fourteen meters from the CAPS
       * door with a three-meter fix and was refused for a 374-second reading,
       * because his phone had been holding the delivery for six minutes. The
       * rule meant to stop someone completing a stop from the wrong place was
       * punishing a man standing in the right one.
       *
       * The anti-cheat question is "was this reading fresh when you captured
       * the proof", and queuedAt is when that happened. Time in the queue is a
       * property of the signal, not of the driver, and it is reported
       * separately below.
       *
       * Clamped at zero: attachMissingFixes() can attach a fix AFTER an item
       * was queued -- a delivery captured with no satellite lock at all, given
       * a position when the driver got back to the van -- and that arrives with
       * a fix newer than the queue entry.
       */
      fixAgeS: Math.max(0, Math.round((item.queuedAt - fix.capturedAt) / 1000)),
      /* How long it then waited for signal. Recorded, never used to refuse. */
      queuedS: Math.max(0, Math.round((Date.now() - item.queuedAt) / 1000)),
      proofPhotoPath,
      recipientName: item.recipientName,
      signaturePath,
      // Accepts both shapes: anything queued before this version has `barcode`.
      barcodes: item.barcodes ?? (item.barcode ? [item.barcode] : []),
      barcodeException: item.barcodeException,
      // `?? null` rather than a truthiness test: zero totes back is a real
      // morning, and it must not be sent as "not counted".
      totesReturned: item.totesReturned ?? null,
      // Face scan gate (134): this build keeps the delivery if refused for it.
      scanGate: 1,
      scanPending: await hasPendingScanFor(item.taskId),
    }),
  }, token);

  if (res.ok) {
    const body = (await res.json()) as {
      ok: boolean; reason?: string; distance_m?: number; radius_m?: number;
    };
    // A definitive server verdict -- accepted OR gate-rejected -- removes the
    // item; retrying an unchanged submission cannot change a gate's answer.
    // The reason travels back so the driver is told what to actually do.
    if (body.ok) return { status: 'done' };
    return {
      status: 'rejected',
      reason: body.reason ?? 'rejected',
      distanceM: body.distance_m,
      radiusM: body.radius_m,
    };
  }
  /*
   * Before anything else. Without this a 401 fell through to the line below and
   * was read as the server REFUSING the delivery, so the queued item — photo,
   * signature and all — was deleted. A finished delivery must never be thrown
   * away because a token expired.
   */
  throwIfExpired(res.status);
  throwIfGone(res.status);
  return res.status >= 500
    ? { status: 'retry' }
    : { status: 'rejected', reason: 'server_refused_' + res.status };
}

export interface FlushResult {
  /**
   * The driver must sign in again. Every queued item is still queued: signing
   * in and reopening the app sends them.
   */
  authExpired?: boolean;
  synced: number;
  rejected: Array<{
    taskId: string; error: string; distanceM?: number; radiusM?: number;
  }>;
  remaining: number;
  /** Stops whose delivery is waiting on the phone for a face scan (134). */
  needsScan?: string[];
}

let flushing = false;
/*
 * 2 Nov: A SEND STUCK IN THE BACKGROUND BLOCKED EVERY RETRY BEHIND IT.
 *
 * Abdul's photo upload sat 12 minutes (limit 45 s) after his iPhone was
 * locked -- the phone freezes the timer that should cancel it -- and while it
 * sat, opening the app, getting signal and every other trigger found
 * `flushing` true and did nothing. His delivery from 7:16 pm reached us at
 * 5:10 pm the next day. Measured by the wall clock, which keeps moving while
 * the phone sleeps: a send older than this is treated as dead and a new one
 * starts. A duplicate is safe; the server answers "already done".
 */
const STUCK_MS = 90_000;
let flushStartedAt = 0;
let flushGen = 0;

/*
 * Where the current send is, for diagnostics only (3 Oct). A timeout used to
 * say just "complete <id>" -- not whether the photo would not upload, the
 * confirmation would not go, or the photo could not even be read back off the
 * phone. Catherine Cruz's deliveries waited minutes to hours and that was the
 * one question nobody could answer.
 */
const progress = { stage: 'start', t0: 0, photoKB: 0 };
const elapsed = () => (progress.t0 ? Date.now() - progress.t0 : 0);

export async function flushQueue(): Promise<FlushResult> {
  const result: FlushResult = { synced: 0, rejected: [], remaining: 0 };
  if (flushing && Date.now() - flushStartedAt < STUCK_MS) return result;   // single-flight per context
  flushing = true;
  flushStartedAt = Date.now();
  const gen = ++flushGen;
  try {
    const token = await getToken();
    const items = await peekAll();
    if (!token) { result.remaining = items.length; return result; }

    // Copies of a stop already sent in this pass — skipped, never re-sent.
    const settled = new Set<string>();
    for (const item of items) {
      if (settled.has(item.id)) continue;
      try {
        const verdict = await submitOne(item, token);
        if (verdict.status === 'done') {
          await remove(item.id);
          result.synced += 1;
          markDoneLocally(item.taskId, item.kind === 'skip' ? 'skipped' : 'completed');
          // Any other copy of the SAME stop is now redundant (4 Oct).
          for (const other of items) {
            if (other.id !== item.id && other.taskId === item.taskId && other.kind === item.kind) {
              settled.add(other.id);
              await remove(other.id);
            }
          }
          // A send that took over ten seconds: how long, how big, how many tries.
          if (item.kind === 'complete' && elapsed() > 10_000) {
            reportError('diag_sync', 'sync', {
              ok: true, ms: elapsed(), photoKB: progress.photoKB, tries: item.attempts + 1,
            });
          }
        } else if (verdict.status === 'rejected' && verdict.reason === 'face_scan_required') {
          // NOT removed: the delivery waits on the phone for its face scan.
          markScanNeeded(item.taskId);
          result.needsScan = [...(result.needsScan ?? []), item.taskId];
          reportError('face_scan_required', 'sync', `task ${item.taskId}`);
        } else if (verdict.status === 'rejected') {
          // Surface to the driver instead of silently retrying forever.
          await remove(item.id);
          result.rejected.push({
            taskId: item.taskId,
            error: verdict.reason,
            distanceM: verdict.distanceM,
            radiusM: verdict.radiusM,
          });
        } else if (item.attempts + 1 >= MAX_ATTEMPTS) {
          await remove(item.id);
          result.rejected.push({ taskId: item.taskId, error: 'max_attempts' });
        } else {
          await markFailure(item, 'network');
        }
      } catch (err) {
        /*
         * An expired session stops the whole pass immediately. Every remaining
         * item is LEFT IN THE QUEUE and its attempt count is NOT burned: none
         * of them can succeed until the driver signs in, and each extra try
         * only spends one of the 50 attempts that stand between their work and
         * 'max_attempts'.
         */
        /*
         * The stop is gone. Drop the item and TELL the driver, rather than
         * retrying forever or deleting it in silence. Whatever they captured
         * cannot be delivered to a task that no longer exists, and they are
         * owed the truth about that.
         */
        if (err instanceof TaskGone) {
          await remove(item.id);
          reportError('task_gone', 'sync', `${item.kind} ${item.taskId}`);
          result.rejected.push({ taskId: item.taskId, error: 'task_gone' });
          continue;
        }
        if (err instanceof SessionExpired) {
          await clearToken();
          reportError('auth_expired', 'sync', `${item.kind} ${item.taskId}`);
          result.authExpired = true;
          break;
        }
        const msg = (err as Error).message;
        /*
         * A picture the phone itself has lost (10 Oct, Temessa Guzman: 16
         * failed sends in 11 minutes behind a "no signal" screen). It can
         * never be sent, and while it sits in the queue the stop shows
         * "sending" and cannot be redone. Drop it, record it, and give the
         * stop back to the driver to do again.
         */
        if (item.kind === 'complete'
            && /can not be found|Blob\/File data|NotReadable|NotFoundError/i.test(String(msg))) {
          reportError('diag_sync', 'sync', {
            ok: false, stage: progress.stage, error: 'proof_lost', task: item.taskId.slice(0, 8),
            tries: item.attempts + 1, detail: String(msg).slice(0, 80),
          });
          await remove(item.id).catch(() => undefined);
          result.rejected.push({ taskId: item.taskId, error: 'proof_lost' });
          continue;
        }
        // A timeout here is the shape of the no-signal freeze: worth recording
        // every time, because it is what we could never see before.
        if (msg === 'network_timeout') {
          reportError('request_timeout', 'sync',
            `${item.kind} ${item.taskId} stage=${progress.stage} ms=${elapsed()} photoKB=${progress.photoKB}`);
        } else {
          /*
           * Any OTHER failure was never reported -- including a photo that the
           * phone could not read back out of its own storage. That is the
           * likeliest cause of the waits, and it was invisible.
           */
          reportError('diag_sync', 'sync', {
            ok: false, stage: progress.stage, ms: elapsed(), photoKB: progress.photoKB,
            tries: item.attempts + 1, error: String(msg).slice(0, 160),
          });
        }
        await markFailure(item, msg).catch(() => undefined);
      }
    }
    result.remaining = (await peekAll()).length;
    return result;
  } finally {
    if (gen === flushGen) flushing = false;   // a newer send owns the flag
  }
}
